/**
 * NDT batch 6 — the AI digests (fieldiq-api#1177 6a, #1178 6b). Typed by hand
 * from the API at c5632d0: routes/ndt-inspection-ai.routes.ts,
 * lib/ndt-ai-digests.ts (RepairRateDigest, CertExpiryDigest, shapeDigestRun) and
 * lib/ndt-ai-review.ts (DoseReview, TicketPreflight). The manifest records these
 * routes as 403 for the probe tenant (no `ndt_inspection`), so nothing is
 * generated for them.
 *
 * camelCase, office surface, no `meta`. Every figure here is the API's. Doses
 * are in mSv and survey rates in µSv/h, as the field names say: the digest does
 * not convert to the tenant's unit system.
 */

export const NDT_AI_AGENTS = ['ndt_repair_rate_digest', 'ndt_cert_expiry_digest', 'ndt_dose_review', 'ndt_ticket_preflight'] as const;
export type NdtAiAgent = (typeof NDT_AI_AGENTS)[number];

/** agent_runs.outcome. A digest is reviewed through /agent-runs/:id/approve|dismiss. */
export type NdtAiOutcome = 'pending' | 'actioned' | 'dismissed' | 'superseded' | 'no_action';

export interface NdtDigestDraft {
  narrative: string;
  observations: string[];
}

export interface NdtDoseNote {
  userId: string;
  workerName: string;
  note: string;
}
export interface NdtDoseDraft {
  narrative: string;
  notes: NdtDoseNote[];
}

// ─── Repair-rate digest ─────────────────────────────────────────────────────

export interface NdtRepairRate {
  examined: number;
  repaired: number;
  /** null when nothing was examined. A fraction: 0.125 = 12.5%. */
  rate: number | null;
  smallSample: boolean;
}
export interface NdtRateGroup extends NdtRepairRate {
  key: string;
  label: string;
}
export type NdtRepairFlagKind = 'welder_above_peers' | 'line_above_peers' | 'month_increase';
export interface NdtRepairFlag {
  kind: NdtRepairFlagKind;
  key: string;
  label: string;
  rate: number;
  examined: number;
  repaired: number;
  comparedTo: { label: string; rate: number; examined: number; repaired: number };
  /** Fraction points above the comparison: 0.07 = 7 points. */
  points: number;
}
export interface NdtRepairRateDigest {
  from: string;
  to: string;
  overall: NdtRepairRate;
  byWelder: NdtRateGroup[];
  byLine: NdtRateGroup[];
  byMonth: NdtRateGroup[];
  flags: NdtRepairFlag[];
  smallSampleGroups: { welders: number; lines: number; months: number };
  smallSampleWelds: number;
}

// ─── Compliance (cert expiry) digest ────────────────────────────────────────

export interface NdtExpiryItem {
  id: string;
  name: string;
  detail: string;
  /** expired, due_soon, lapsed (welder continuity), overdue (leak test, calibration), never (never calibrated). */
  status: string;
  day: string | null;
  daysLeft: number | null;
}
export interface NdtCertExpiryDigest {
  today: string;
  techCerts: NdtExpiryItem[];
  welderQuals: NdtExpiryItem[];
  leakTests: NdtExpiryItem[];
  calibrations: NdtExpiryItem[];
  total: number;
}

// ─── Dose review ────────────────────────────────────────────────────────────

export type NdtDoseStatus = 'over_level' | 'approaching_level' | 'below_level';
export interface NdtDoseRecord {
  id: string;
  periodStart: string;
  periodEnd: string;
  dosimeterKind: string;
  deepDoseMsv: number;
  shallowDoseMsv: number | null;
  status: NdtDoseStatus | null;
}
export interface NdtSurveyAnomaly {
  logId: string;
  sourceId: string;
  source: string;
  event: string;
  userId: string | null;
  userName: string | null;
  day: string;
  readingUsvH: number;
  peerReadings: number;
  peerMedianUsvH: number;
  ratio: number;
}
export interface NdtWorkerDose {
  userId: string;
  name: string;
  records: NdtDoseRecord[];
  totalDeepMsv: number;
  maxDeepMsv: number;
  status: NdtDoseStatus | null;
  surveyAnomalies: NdtSurveyAnomaly[];
  flagged: boolean;
}
export interface NdtDoseReview {
  from: string;
  to: string;
  levelConfigured: boolean;
  investigationLevelMsv: number | null;
  approachFraction: number;
  workers: NdtWorkerDose[];
  unattributedAnomalies: NdtSurveyAnomaly[];
  survey: { readings: number; compared: number; notCompared: number; anomalies: number; ratio: number; minPeerReadings: number };
  flaggedWorkers: number;
}

// ─── Ticket preflight ───────────────────────────────────────────────────────

export interface NdtPreflight {
  ticket: { id: string; ticketNumber: string; jobId: string; jobNumber: string; scope: string; workDate: string | null; status: string };
  liveLines: number;
  unpricedLines: { id: string; description: string; unit: string; method: string | null; workDate: string | null; reason: string | null }[];
  unclaimedExams: { id: string; weldNumber: string; method: string; cycle: string; result: string; workDate: string }[];
  pendingExams: number;
  ready: boolean;
}

// ─── The run ────────────────────────────────────────────────────────────────

/** What every POST returns (201). No outcome or timestamps — read the stored run for those. */
export interface NdtAiRunResult<D = unknown, R = NdtDigestDraft | NdtDoseDraft> {
  agentRunId: string;
  agent: NdtAiAgent;
  status: 'draft';
  facts: unknown;
  draft: R | null;
  aiError: string | null;
  data: D;
}

/** GET /ai/digests and /ai/digests/:id (shapeDigestRun). */
export interface NdtAiDigest {
  agentRunId: string;
  agent: string;
  status: string;
  outcome: NdtAiOutcome | string;
  trigger: string | null;
  triggerData: Record<string, unknown> | null;
  data: unknown;
  draft: NdtDigestDraft | NdtDoseDraft | null;
  aiError: string | null;
  createdAt: string | null;
  reviewedAt: string | null;
}

export interface NdtAiWindow {
  from?: string;
  to?: string;
}
